import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  MiniGameType, MiniGameSession, MiniGameAnswer, MiniGameStudent, StudentTeam,
  SpinWheelState, TugOfWarState, LightningRoundState, BombPassState,
  MysteryBoxState, BoxModifier, StudentScoreEntry, RoundOutcomeMeta
} from '../interfaces/mini-game.interface';
import { GAME_SKILL_MAP } from '../data/game-skill-map';
import {
  BowlingMode, BowlingParticipant, BowlingPhase, BowlingState
} from '../interfaces/bowling.interface';
import {
  advanceBowlingTurn, applyRoll, buildEmptyFrames, determineWinner,
  refreshTotals
} from './bowling-score';
import { QuizSubject, GameQuestion, DifficultyLevel } from '../interfaces/question.interface';
import { SymmetryState } from '../interfaces/symmetry.interface';
import { buildSymmetryRounds } from '../data/symmetry-shapes';
import { MatchUpState } from '../interfaces/match-up.interface';
import { buildMatchUpRounds } from '../data/match-up-rounds';
import { FractionLineState } from '../interfaces/fraction-line.interface';
import { buildFractionLineRounds } from '../data/fraction-line-rounds';
import { FractionHopsState } from '../interfaces/fraction-hops.interface';
import { buildFractionHopsRounds } from '../data/fraction-hops-rounds';
import { FractionTrekState } from '../interfaces/fraction-trek.interface';
import { buildFractionTrekRounds } from '../data/fraction-trek-rounds';
import { ZoomQuestState } from '../interfaces/zoom-quest.interface';
import { buildZoomQuestRounds } from '../data/zoom-quest-rounds';
import { TreasureGridState } from '../interfaces/treasure-grid.interface';
import { buildTreasureGridRounds } from '../data/treasure-grid-rounds';
import { CubeFillState } from '../interfaces/cube-fill.interface';
import { buildCubeFillRounds } from '../data/cube-fill-rounds';
import { BalanceItState } from '../interfaces/balance-it.interface';
import { buildBalanceItRounds } from '../data/balance-it-rounds';
import { PowerBurstState } from '../interfaces/power-burst.interface';
import { buildPowerBurstRounds } from '../data/power-burst-rounds';
import { CuttingPizzaState } from '../interfaces/cutting-pizza.interface';
import { buildCuttingPizzaRounds } from '../data/cutting-pizza-rounds';
import { VaultCrackerState } from '../interfaces/vault-cracker.interface';
import { buildVaultCrackerRounds } from '../data/vault-cracker-rounds';
import { GameModeService } from './game-mode.service';
import { QuestionBankService } from './question-bank.service';
import { ScoringService } from './scoring.service';
import { GameStatePersistenceService } from './game-state-persistence.service';
import { ClassStudentsService } from './class-students.service';
import { getMiniGameConfig, MINI_GAME_CONFIGS } from '../data/mini-game-config';
import { GamePhase } from '../interfaces/game-state.interface';
import { AmplitudeService } from 'src/app/_services/amplitude.service';

// Auto-cycle game rotation order. Bowling sits at the front so the
// 3D mini-game greets the user first when auto-start kicks in from
// the calling screen — a stronger hook than launching straight into
// a quiz-flavoured game.
const GAME_ROTATION: MiniGameType[] = [
  MiniGameType.BOWLING,
  MiniGameType.BOMB_PASS,
  MiniGameType.MYSTERY_BOX,
  MiniGameType.SPIN_WHEEL,
  MiniGameType.TUG_OF_WAR,
  MiniGameType.LIGHTNING_ROUND
];

@Injectable({ providedIn: 'root' })
export class MiniGameService {

  private _session = new BehaviorSubject<MiniGameSession | null>(null);
  session$ = this._session.asObservable();

  private _currentGameType = new BehaviorSubject<MiniGameType | null>(null);
  currentGameType$ = this._currentGameType.asObservable();

  private _selectedSubject = new BehaviorSubject<QuizSubject | 'random'>('random');
  selectedSubject$ = this._selectedSubject.asObservable();

  private _lastResult = new BehaviorSubject<{ isCorrect: boolean; xpEarned: number } | null>(null);
  lastResult$ = this._lastResult.asObservable();

  private _studentScores = new BehaviorSubject<StudentScoreEntry[]>([]);
  studentScores$ = this._studentScores.asObservable();

  private _currentStudentId = new BehaviorSubject<number | null>(null);
  currentStudentId$ = this._currentStudentId.asObservable();

  private studentRotationIndex = 0;

  // Auto-cycle state
  private _autoCycleEnabled = new BehaviorSubject<boolean>(false);
  autoCycleEnabled$ = this._autoCycleEnabled.asObservable();
  private autoCycleIndex = 0;

  private timerInterval: any;
  private _timerValue = new BehaviorSubject<number>(0);
  timerValue$ = this._timerValue.asObservable();

  private _timerExpired = new Subject<void>();
  timerExpired$ = this._timerExpired.asObservable();

  private _leaderboardOpen = new BehaviorSubject<boolean>(true);
  leaderboardOpen$ = this._leaderboardOpen.asObservable();

  setLeaderboardOpen(open: boolean): void {
    this._leaderboardOpen.next(open);
  }

  constructor(
    private gameModeService: GameModeService,
    private questionBankService: QuestionBankService,
    private scoringService: ScoringService,
    private persistenceService: GameStatePersistenceService,
    private classStudentsService: ClassStudentsService,
    private amplitudeService: AmplitudeService
  ) {}

  // --- Auto-Cycle ---

  enableAutoCycle(): void {
    this._autoCycleEnabled.next(true);
    this.autoCycleIndex = 0;
  }

  disableAutoCycle(): void {
    this._autoCycleEnabled.next(false);
  }

  isAutoCycleEnabled(): boolean {
    return this._autoCycleEnabled.getValue();
  }

  getNextGameConfig(): { type: MiniGameType; name: { en: string; ar: string }; icon: string } | null {
    if (!this._autoCycleEnabled.getValue()) return null;
    const gameType = GAME_ROTATION[this.autoCycleIndex % GAME_ROTATION.length];
    const config = getMiniGameConfig(gameType);
    return { type: config.type, name: config.name, icon: config.icon };
  }

  startNextAutoCycleGame(): void {
    const gameType = GAME_ROTATION[this.autoCycleIndex % GAME_ROTATION.length];
    this.autoCycleIndex++;
    this.setSelectedSubject('random');

    if (gameType === MiniGameType.TUG_OF_WAR) {
      const classId = this.gameModeService.getCurrentClassId();
      let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
      if (!students.length) students = this.classStudentsService.getAllStudents();
      const teams = this.classStudentsService.generateTeams(students);
      this.startTugOfWarWithTeams(teams, students);
    } else if (gameType === MiniGameType.BOWLING) {
      // Auto-cycle bowling: skip the manual setup screen (mode toggle
      // + drag-drop team builder) and roll straight into the 3D
      // scene. Falls back to ONE_VS_ONE if the class has fewer than
      // two students for some edge case.
      const classId = this.gameModeService.getCurrentClassId();
      let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
      if (!students.length) students = this.classStudentsService.getAllStudents();
      this.startBowlingWithAutoTeams(students);
    } else {
      this.startMiniGame(gameType);
    }
  }

  /**
   * Auto-cycle entry point for Bowling — picks default team names +
   * colours, splits the class into two halves via
   * `ClassStudentsService.generateTeams`, and calls
   * `startBowlingGame` directly. Bypasses `BowlingSetupComponent`'s
   * manual drag-drop team builder so the auto-cycle flows smoothly.
   */
  startBowlingWithAutoTeams(students: MiniGameStudent[]): void {
    const mode = students.length >= 2 ? BowlingMode.TEAM_VS_TEAM : BowlingMode.ONE_VS_ONE;
    const teams = this.classStudentsService.generateTeams(students);
    const sideA: BowlingParticipant = {
      id: 'A',
      displayName: { en: 'Blue Team', ar: 'الفريق الأزرق' },
      color: '#2196F3',
      students: teams.teamA.students.map(s => ({ ...s, teamId: 'A' as const })),
      frames: buildEmptyFrames(),
      currentStudentIdx: 0,
      totalScore: 0
    };
    const sideB: BowlingParticipant = {
      id: 'B',
      displayName: { en: 'Red Team', ar: 'الفريق الأحمر' },
      color: '#F44336',
      students: teams.teamB.students.map(s => ({ ...s, teamId: 'B' as const })),
      frames: buildEmptyFrames(),
      currentStudentIdx: 0,
      totalScore: 0
    };
    this.startBowlingGame(mode, [sideA, sideB]);
  }

  setSelectedSubject(subject: QuizSubject | 'random'): void {
    this._selectedSubject.next(subject);
  }

  getSelectedSubject(): QuizSubject | 'random' {
    return this._selectedSubject.getValue();
  }

  private gameEventName: Record<string, string> = {
    [MiniGameType.BOMB_PASS]: 'bombPass',
    [MiniGameType.MYSTERY_BOX]: 'mysteryBox',
    [MiniGameType.SPIN_WHEEL]: 'spinWheel',
    [MiniGameType.TUG_OF_WAR]: 'tugOfWar',
    [MiniGameType.LIGHTNING_ROUND]: 'lightningRound',
    [MiniGameType.CLASSIC_QUIZ]: 'classicQuiz',
    [MiniGameType.BOWLING]: 'bowling',
    [MiniGameType.SYMMETRY]: 'symmetry',
    [MiniGameType.MATCH_UP]: 'matchUp',
    [MiniGameType.FRACTION_LINE]: 'fractionLine',
    [MiniGameType.FRACTION_HOPS]: 'fractionHops',
    [MiniGameType.FRACTION_TREK]: 'fractionTrek',
    [MiniGameType.ZOOM_QUEST]: 'zoomQuest',
    [MiniGameType.TREASURE_GRID]: 'treasureGrid',
    [MiniGameType.CUBE_FILL]: 'cubeFill',
    [MiniGameType.BALANCE_IT]: 'balanceIt',
    [MiniGameType.POWER_BURST]: 'powerBurst',
    [MiniGameType.VAULT_CRACKER]: 'vaultCracker',
  };

  /**
   * Emit the shared per-round skill-analytics event (`gameWorld-round`).
   *
   * This is the ONE place the shared attribute contract is assembled, so a
   * game cannot ship a round event missing it: student identity + grade,
   * the skill (from GAME_SKILL_MAP), difficulty, timing/tries (from the
   * shell base's `roundMeta`), xp and class are all stamped here. Each game
   * passes only its per-game `extras` (e.g. denominator/operation). Adoption
   * stays on the existing `-launch`/`-finished` events; this is the skill
   * layer. See ig-game-theme.md §3 (Skill analytics rule).
   */
  /**
   * Shared class/grade context stamped onto every per-game `-launch`/`-finished`
   * event emitted from this service (and, via `trackRoundOutcome`, `gameWorld-round`),
   * so all analytics carry the same human-readable labels alongside the numeric keys.
   * `className`/`gradeName` are single display strings (no Arabic variant); null before
   * class selection. Does NOT touch the global `AmplitudeService.trackEvent`.
   */
  private gameContext(): { classId: number | null; className: string | null; gradeName: string | null } {
    return {
      classId: this.gameModeService.getCurrentClassId() ?? null,
      className: this.gameModeService.getCurrentClassName() ?? null,
      gradeName: this.gameModeService.getCurrentGradeName() ?? null
    };
  }

  private trackRoundOutcome(
    gameType: MiniGameType,
    state: {
      rounds: ReadonlyArray<{ difficulty: DifficultyLevel }>;
      currentRound: number;
      currentStudentId: number | null;
    },
    solved: boolean,
    xpEarned: number,
    meta: RoundOutcomeMeta,
    extras: Record<string, any> = {}
  ): void {
    const session = this._session.getValue();
    const student = session?.students.find(s => s.id === state.currentStudentId) ?? null;
    const round = state.rounds[state.currentRound];
    const skillMeta = GAME_SKILL_MAP[gameType];

    const payload = {
      studentId: student?.id ?? null,
      studentName: student?.name?.en ?? null,
      studentNameAr: student?.name?.ar ?? null,
      grade: student?.grade_id ?? skillMeta?.grade ?? null,
      game: gameType,
      skill: skillMeta?.skill ?? null,
      difficulty: round?.difficulty ?? null,
      solved,
      failedTries: meta.failedTries,
      reactionTimeMs: meta.reactionTimeMs,
      timeToCorrectMs: meta.timeToCorrectMs,
      xpEarned,
      classId: this.gameModeService.getCurrentClassId() ?? null,
      gradeName: this.gameModeService.getCurrentGradeName() ?? null,
      className: this.gameModeService.getCurrentClassName() ?? null,
      ...extras
    };

    // Payload is observable in dev even though trackEvent only transmits on
    // the live branch (environment.live) — lets us verify the shape locally.
    // console.log (not debug: Chrome hides debug behind the Verbose level).
    console.log('[gameWorld-round]', payload);
    this.amplitudeService.trackEvent('gameWorld-round', payload);
  }

  startMiniGame(gameType: MiniGameType): void {
    const eventKey = this.gameEventName[gameType];
    if (eventKey) {
      this.amplitudeService.trackEvent(`gameWorld-${eventKey}-launch`, { ...this.gameContext() });
    }

    if (gameType === MiniGameType.CLASSIC_QUIZ) {
      this.gameModeService.startQuiz(this._selectedSubject.getValue());
      return;
    }

    const config = getMiniGameConfig(gameType);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    // Fallback to all students when class has no match (dummy data)
    if (!students.length) {
      students = this.classStudentsService.getAllStudents();
    }
    const questions = this.questionBankService.getQuestions({
      subject: this._selectedSubject.getValue(),
      count: config.questionsCount
    });

    if (questions.length === 0) return;

    const session: MiniGameSession = {
      gameType,
      config,
      students,
      subject: this._selectedSubject.getValue(),
      questions,
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: this.initGameState(gameType, students, config)
    };

    if (gameType === MiniGameType.TUG_OF_WAR) {
      session.teams = this.classStudentsService.generateTeams(students);
    }

    this._currentGameType.next(gameType);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  startTugOfWarWithTeams(
    teams: { teamA: StudentTeam; teamB: StudentTeam },
    allStudents: MiniGameStudent[]
  ): void {
    this.amplitudeService.trackEvent('gameWorld-tugOfWar-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.TUG_OF_WAR);
    const questions = this.questionBankService.getQuestions({
      subject: this._selectedSubject.getValue(),
      count: config.questionsCount
    });
    if (questions.length === 0) return;

    const session: MiniGameSession = {
      gameType: MiniGameType.TUG_OF_WAR,
      config,
      students: allStudents,
      teams,
      subject: this._selectedSubject.getValue(),
      questions,
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: this.initGameState(MiniGameType.TUG_OF_WAR, allStudents, config)
    };

    this._currentGameType.next(MiniGameType.TUG_OF_WAR);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(allStudents);
    this.studentRotationIndex = 0;
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  goToTeamSetup(): void {
    this._currentGameType.next(MiniGameType.TUG_OF_WAR);
    this.gameModeService.setPhase(GamePhase.TEAM_SETUP);
  }

  // --- Bowling ---

  goToBowlingSetup(): void {
    this.amplitudeService.trackEvent('gameWorld-bowling-setup', {});
    this._currentGameType.next(MiniGameType.BOWLING);
    this.gameModeService.setPhase(GamePhase.TEAM_SETUP);
  }

  startBowlingGame(
    mode: BowlingMode,
    participants: [BowlingParticipant, BowlingParticipant]
  ): void {
    this.amplitudeService.trackEvent('gameWorld-bowling-launch', { ...this.gameContext(), mode });
    const config = getMiniGameConfig(MiniGameType.BOWLING);
    const allStudents = [
      ...participants[0].students,
      ...participants[1].students
    ];

    participants.forEach(p => {
      p.frames = buildEmptyFrames();
      p.currentStudentIdx = 0;
      p.totalScore = 0;
    });

    const state: BowlingState = {
      mode,
      participants,
      currentFrame: 0,
      currentSide: 0,
      pinsStandingBefore: Array(10).fill(true),
      lastRollPins: 0,
      phase: BowlingPhase.IDLE,
      winnerSide: null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.BOWLING,
      config,
      students: allStudents,
      subject: 'random',
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.BOWLING);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(allStudents);
    this.studentRotationIndex = 0;
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  recordBowlingRoll(pinsKnocked: number, _knockedPinIndices: number[] = []): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.BOWLING) return;

    const state = session.gameState as BowlingState;
    if (state.currentFrame >= 10) return;

    const side = state.currentSide;
    const p = state.participants[side];
    const frame = p.frames[state.currentFrame];

    applyRoll(frame, pinsKnocked);
    state.lastRollPins = pinsKnocked;
    refreshTotals(p);

    advanceBowlingTurn(state);
    // Each turn is one roll on a fresh full rack — no carryover leave from
    // a previous "first roll" since there's no second roll any more.
    state.pinsStandingBefore = Array(10).fill(true);

    if (state.currentFrame >= 10) {
      state.phase = BowlingPhase.FINISHED;
      state.winnerSide = determineWinner(state.participants);
      this.amplitudeService.trackEvent('gameWorld-bowling-finished', {
        ...this.gameContext(),
        winnerSide: state.winnerSide,
        scoreA: state.participants[0].totalScore,
        scoreB: state.participants[1].totalScore
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
    }

    this._session.next(session);
  }

  // --- Mirror Match (Symmetry) ---

  /**
   * Launch Mirror Match straight into play (no setup screen). Builds a
   * shuffled round list + a student turn-order from the current class,
   * seeds `SymmetryState`, and jumps to `MINI_GAME_ACTIVE`.
   */
  startSymmetryGame(): void {
    this.amplitudeService.trackEvent('gameWorld-symmetry-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.SYMMETRY);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();

    const rounds = buildSymmetryRounds(config.questionsCount);
    const totalRounds = rounds.length;
    const order = students.map(s => s.id).sort(() => Math.random() - 0.5);

    const state: SymmetryState = {
      rounds,
      currentRound: 0,
      totalRounds,
      solved: new Array(totalRounds).fill(false),
      studentOrder: order,
      currentStudentId: order.length ? order[0] : null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.SYMMETRY,
      config,
      students,
      subject: QuizSubject.MATH,
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.SYMMETRY);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    if (state.currentStudentId != null) this.setCurrentStudent(state.currentStudentId);
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  /**
   * Record the outcome of the current round for the acting student —
   * XP, streak, leaderboard, answer log — WITHOUT advancing. The shell
   * calls `advanceSymmetryRound` after its win / skip animation.
   */
  scoreSymmetryRound(solved: boolean): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.SYMMETRY) return;
    const state = session.gameState as SymmetryState;
    const shape = state.rounds[state.currentRound];
    if (!shape) return;

    const classId = this.gameModeService.getCurrentClassId();
    const creature = classId ? this.persistenceService.updateStreak(classId, solved) : null;
    const streak = creature?.currentStreak || 0;

    const score = this.scoringService.calculateXP({
      difficulty: shape.difficulty as DifficultyLevel,
      streak,
      questionSubject: QuizSubject.MATH,
      timeRemaining: 0,
      timeLimit: 0,
      isCorrect: solved
    });
    const xpEarned = score.totalXP;

    if (classId && xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) this.gameModeService['_creature'].next(updated);
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const studentId = state.currentStudentId ?? undefined;
    session.answers.push({
      questionId: shape.id,
      selectedOptionId: null,
      isCorrect: solved,
      xpEarned,
      studentId
    });
    session.totalXP += xpEarned;
    state.solved[state.currentRound] = solved;

    if (studentId != null) this.updateStudentScore(studentId, xpEarned, solved);

    this._session.next(session);
    this._lastResult.next({ isCorrect: solved, xpEarned });
  }

  /** Advance to the next round + student, or finish the game. */
  advanceSymmetryRound(): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.SYMMETRY) return;
    const state = session.gameState as SymmetryState;

    state.currentRound++;
    if (state.currentRound >= state.totalRounds) {
      this.amplitudeService.trackEvent('gameWorld-symmetry-finished', {
        ...this.gameContext(),
        solved: state.solved.filter(Boolean).length,
        total: state.totalRounds
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    if (state.studentOrder.length) {
      state.currentStudentId = state.studentOrder[state.currentRound % state.studentOrder.length];
      this.setCurrentStudent(state.currentStudentId);
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  // --- Star Match (drag numbers & letters) ---

  /** Launch Star Match straight into play (no setup screen). */
  startMatchUpGame(): void {
    this.amplitudeService.trackEvent('gameWorld-matchUp-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.MATCH_UP);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();

    const rounds = buildMatchUpRounds(config.questionsCount);
    const totalRounds = rounds.length;
    const order = students.map(s => s.id).sort(() => Math.random() - 0.5);

    const state: MatchUpState = {
      rounds,
      currentRound: 0,
      totalRounds,
      solved: new Array(totalRounds).fill(false),
      studentOrder: order,
      currentStudentId: order.length ? order[0] : null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.MATCH_UP,
      config,
      students,
      subject: QuizSubject.GENERAL,
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.MATCH_UP);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    if (state.currentStudentId != null) this.setCurrentStudent(state.currentStudentId);
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  /** Record the current round's outcome for the acting student. */
  scoreMatchUpRound(solved: boolean): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.MATCH_UP) return;
    const state = session.gameState as MatchUpState;
    const round = state.rounds[state.currentRound];
    if (!round) return;

    const classId = this.gameModeService.getCurrentClassId();
    const creature = classId ? this.persistenceService.updateStreak(classId, solved) : null;
    const streak = creature?.currentStreak || 0;

    const score = this.scoringService.calculateXP({
      difficulty: round.difficulty as DifficultyLevel,
      streak,
      questionSubject: QuizSubject.GENERAL,
      timeRemaining: 0,
      timeLimit: 0,
      isCorrect: solved
    });
    const xpEarned = score.totalXP;

    if (classId && xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) this.gameModeService['_creature'].next(updated);
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const studentId = state.currentStudentId ?? undefined;
    session.answers.push({
      questionId: round.id,
      selectedOptionId: null,
      isCorrect: solved,
      xpEarned,
      studentId
    });
    session.totalXP += xpEarned;
    state.solved[state.currentRound] = solved;

    if (studentId != null) this.updateStudentScore(studentId, xpEarned, solved);

    this._session.next(session);
    this._lastResult.next({ isCorrect: solved, xpEarned });
  }

  /** Advance to the next round + student, or finish the game. */
  advanceMatchUpRound(): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.MATCH_UP) return;
    const state = session.gameState as MatchUpState;

    state.currentRound++;
    if (state.currentRound >= state.totalRounds) {
      this.amplitudeService.trackEvent('gameWorld-matchUp-finished', {
        ...this.gameContext(),
        solved: state.solved.filter(Boolean).length,
        total: state.totalRounds
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    if (state.studentOrder.length) {
      state.currentStudentId = state.studentOrder[state.currentRound % state.studentOrder.length];
      this.setCurrentStudent(state.currentStudentId);
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  // --- Fraction Blast (place a fraction on a number line) ---

  /** Launch Fraction Blast straight into play (no setup screen). */
  startFractionLineGame(): void {
    this.amplitudeService.trackEvent('gameWorld-fractionLine-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.FRACTION_LINE);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();

    const rounds = buildFractionLineRounds(config.questionsCount);
    const totalRounds = rounds.length;
    const order = students.map(s => s.id).sort(() => Math.random() - 0.5);

    const state: FractionLineState = {
      rounds,
      currentRound: 0,
      totalRounds,
      solved: new Array(totalRounds).fill(false),
      studentOrder: order,
      currentStudentId: order.length ? order[0] : null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.FRACTION_LINE,
      config,
      students,
      subject: QuizSubject.MATH,
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.FRACTION_LINE);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    if (state.currentStudentId != null) this.setCurrentStudent(state.currentStudentId);
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  /** Record the current round's outcome for the acting student. */
  scoreFractionLineRound(solved: boolean): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.FRACTION_LINE) return;
    const state = session.gameState as FractionLineState;
    const round = state.rounds[state.currentRound];
    if (!round) return;

    const classId = this.gameModeService.getCurrentClassId();
    const creature = classId ? this.persistenceService.updateStreak(classId, solved) : null;
    const streak = creature?.currentStreak || 0;

    const score = this.scoringService.calculateXP({
      difficulty: round.difficulty as DifficultyLevel,
      streak,
      questionSubject: QuizSubject.MATH,
      timeRemaining: 0,
      timeLimit: 0,
      isCorrect: solved
    });
    const xpEarned = score.totalXP;

    if (classId && xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) this.gameModeService['_creature'].next(updated);
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const studentId = state.currentStudentId ?? undefined;
    session.answers.push({
      questionId: round.id,
      selectedOptionId: null,
      isCorrect: solved,
      xpEarned,
      studentId
    });
    session.totalXP += xpEarned;
    state.solved[state.currentRound] = solved;

    if (studentId != null) this.updateStudentScore(studentId, xpEarned, solved);

    this._session.next(session);
    this._lastResult.next({ isCorrect: solved, xpEarned });
  }

  /** Advance to the next round + student, or finish the game. */
  advanceFractionLineRound(): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.FRACTION_LINE) return;
    const state = session.gameState as FractionLineState;

    state.currentRound++;
    if (state.currentRound >= state.totalRounds) {
      this.amplitudeService.trackEvent('gameWorld-fractionLine-finished', {
        ...this.gameContext(),
        solved: state.solved.filter(Boolean).length,
        total: state.totalRounds
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    if (state.studentOrder.length) {
      state.currentStudentId = state.studentOrder[state.currentRound % state.studentOrder.length];
      this.setCurrentStudent(state.currentStudentId);
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  // --- Fraction Hops (multiply a whole by a fraction via jumps) ---

  /** Launch Fraction Hops straight into play (no setup screen). */
  startFractionHopsGame(): void {
    this.amplitudeService.trackEvent('gameWorld-fractionHops-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.FRACTION_HOPS);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();

    const rounds = buildFractionHopsRounds(config.questionsCount);
    const totalRounds = rounds.length;
    const order = students.map(s => s.id).sort(() => Math.random() - 0.5);

    const state: FractionHopsState = {
      rounds,
      currentRound: 0,
      totalRounds,
      solved: new Array(totalRounds).fill(false),
      studentOrder: order,
      currentStudentId: order.length ? order[0] : null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.FRACTION_HOPS,
      config,
      students,
      subject: QuizSubject.MATH,
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.FRACTION_HOPS);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    if (state.currentStudentId != null) this.setCurrentStudent(state.currentStudentId);
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  /** Record the current round's outcome for the acting student. */
  scoreFractionHopsRound(solved: boolean): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.FRACTION_HOPS) return;
    const state = session.gameState as FractionHopsState;
    const round = state.rounds[state.currentRound];
    if (!round) return;

    const classId = this.gameModeService.getCurrentClassId();
    const creature = classId ? this.persistenceService.updateStreak(classId, solved) : null;
    const streak = creature?.currentStreak || 0;

    const score = this.scoringService.calculateXP({
      difficulty: round.difficulty as DifficultyLevel,
      streak,
      questionSubject: QuizSubject.MATH,
      timeRemaining: 0,
      timeLimit: 0,
      isCorrect: solved
    });
    const xpEarned = score.totalXP;

    if (classId && xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) this.gameModeService['_creature'].next(updated);
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const studentId = state.currentStudentId ?? undefined;
    session.answers.push({
      questionId: round.id,
      selectedOptionId: null,
      isCorrect: solved,
      xpEarned,
      studentId
    });
    session.totalXP += xpEarned;
    state.solved[state.currentRound] = solved;

    if (studentId != null) this.updateStudentScore(studentId, xpEarned, solved);

    this._session.next(session);
    this._lastResult.next({ isCorrect: solved, xpEarned });
  }

  /** Advance to the next round + student, or finish the game. */
  advanceFractionHopsRound(): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.FRACTION_HOPS) return;
    const state = session.gameState as FractionHopsState;

    state.currentRound++;
    if (state.currentRound >= state.totalRounds) {
      this.amplitudeService.trackEvent('gameWorld-fractionHops-finished', {
        ...this.gameContext(),
        solved: state.solved.filter(Boolean).length,
        total: state.totalRounds
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    if (state.studentOrder.length) {
      state.currentStudentId = state.studentOrder[state.currentRound % state.studentOrder.length];
      this.setCurrentStudent(state.currentStudentId);
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  // --- Fraction Trek (add/subtract fractions on a number line) ---

  /** Launch Fraction Trek straight into play (no setup screen). */
  startFractionTrekGame(): void {
    this.amplitudeService.trackEvent('gameWorld-fractionTrek-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.FRACTION_TREK);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();

    const rounds = buildFractionTrekRounds(config.questionsCount);
    const totalRounds = rounds.length;
    const order = students.map(s => s.id).sort(() => Math.random() - 0.5);

    const state: FractionTrekState = {
      rounds,
      currentRound: 0,
      totalRounds,
      solved: new Array(totalRounds).fill(false),
      studentOrder: order,
      currentStudentId: order.length ? order[0] : null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.FRACTION_TREK,
      config,
      students,
      subject: QuizSubject.MATH,
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.FRACTION_TREK);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    if (state.currentStudentId != null) this.setCurrentStudent(state.currentStudentId);
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  /** Record the current round's outcome for the acting student. */
  scoreFractionTrekRound(solved: boolean): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.FRACTION_TREK) return;
    const state = session.gameState as FractionTrekState;
    const round = state.rounds[state.currentRound];
    if (!round) return;

    const classId = this.gameModeService.getCurrentClassId();
    const creature = classId ? this.persistenceService.updateStreak(classId, solved) : null;
    const streak = creature?.currentStreak || 0;

    const score = this.scoringService.calculateXP({
      difficulty: round.difficulty as DifficultyLevel,
      streak,
      questionSubject: QuizSubject.MATH,
      timeRemaining: 0,
      timeLimit: 0,
      isCorrect: solved
    });
    const xpEarned = score.totalXP;

    if (classId && xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) this.gameModeService['_creature'].next(updated);
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const studentId = state.currentStudentId ?? undefined;
    session.answers.push({
      questionId: round.id,
      selectedOptionId: null,
      isCorrect: solved,
      xpEarned,
      studentId
    });
    session.totalXP += xpEarned;
    state.solved[state.currentRound] = solved;

    if (studentId != null) this.updateStudentScore(studentId, xpEarned, solved);

    this._session.next(session);
    this._lastResult.next({ isCorrect: solved, xpEarned });
  }

  /** Advance to the next round + student, or finish the game. */
  advanceFractionTrekRound(): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.FRACTION_TREK) return;
    const state = session.gameState as FractionTrekState;

    state.currentRound++;
    if (state.currentRound >= state.totalRounds) {
      this.amplitudeService.trackEvent('gameWorld-fractionTrek-finished', {
        ...this.gameContext(),
        solved: state.solved.filter(Boolean).length,
        total: state.totalRounds
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    if (state.studentOrder.length) {
      state.currentStudentId = state.studentOrder[state.currentRound % state.studentOrder.length];
      this.setCurrentStudent(state.currentStudentId);
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  // --- Cutting Pizza (add/subtract fractions by cutting pizza slices) ---

  /** Launch Cutting Pizza straight into play (no setup screen). */
  startCuttingPizzaGame(): void {
    this.amplitudeService.trackEvent('gameWorld-cuttingPizza-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.CUTTING_PIZZA);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();

    const rounds = buildCuttingPizzaRounds(config.questionsCount);
    const totalRounds = rounds.length;
    const order = students.map(s => s.id).sort(() => Math.random() - 0.5);

    const state: CuttingPizzaState = {
      rounds,
      currentRound: 0,
      totalRounds,
      solved: new Array(totalRounds).fill(false),
      studentOrder: order,
      currentStudentId: order.length ? order[0] : null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.CUTTING_PIZZA,
      config,
      students,
      subject: QuizSubject.MATH,
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.CUTTING_PIZZA);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    if (state.currentStudentId != null) this.setCurrentStudent(state.currentStudentId);
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  /** Record the current round's outcome for the acting student. `meta`
   *  carries the shell base's timing/tries for the skill-analytics event. */
  scoreCuttingPizzaRound(
    solved: boolean,
    meta: RoundOutcomeMeta = { failedTries: 0, reactionTimeMs: null, timeToCorrectMs: null }
  ): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.CUTTING_PIZZA) return;
    const state = session.gameState as CuttingPizzaState;
    const round = state.rounds[state.currentRound];
    if (!round) return;

    const classId = this.gameModeService.getCurrentClassId();
    const creature = classId ? this.persistenceService.updateStreak(classId, solved) : null;
    const streak = creature?.currentStreak || 0;

    const score = this.scoringService.calculateXP({
      difficulty: round.difficulty as DifficultyLevel,
      streak,
      questionSubject: QuizSubject.MATH,
      timeRemaining: 0,
      timeLimit: 0,
      isCorrect: solved
    });
    const xpEarned = score.totalXP;

    if (classId && xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) this.gameModeService['_creature'].next(updated);
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const studentId = state.currentStudentId ?? undefined;
    session.answers.push({
      questionId: round.id,
      selectedOptionId: null,
      isCorrect: solved,
      xpEarned,
      studentId
    });
    session.totalXP += xpEarned;
    state.solved[state.currentRound] = solved;

    if (studentId != null) this.updateStudentScore(studentId, xpEarned, solved);

    this.trackRoundOutcome(MiniGameType.CUTTING_PIZZA, state, solved, xpEarned, meta, {
      denominator: round.den,
      operation: round.op,
      aNum: round.aNum,
      bNum: round.bNum,
      answerNum: round.answerNum
    });

    this._session.next(session);
    this._lastResult.next({ isCorrect: solved, xpEarned });
  }

  /** Advance to the next round + student, or finish the game. */
  advanceCuttingPizzaRound(): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.CUTTING_PIZZA) return;
    const state = session.gameState as CuttingPizzaState;

    state.currentRound++;
    if (state.currentRound >= state.totalRounds) {
      this.amplitudeService.trackEvent('gameWorld-cuttingPizza-finished', {
        ...this.gameContext(),
        solved: state.solved.filter(Boolean).length,
        total: state.totalRounds
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    if (state.studentOrder.length) {
      state.currentStudentId = state.studentOrder[state.currentRound % state.studentOrder.length];
      this.setCurrentStudent(state.currentStudentId);
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  // --- Vault Cracker (find a percent of a quantity on a safe dial) ---

  /** Launch Vault Cracker straight into play (no setup screen). */
  startVaultCrackerGame(): void {
    this.amplitudeService.trackEvent('gameWorld-vaultCracker-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.VAULT_CRACKER);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();

    const rounds = buildVaultCrackerRounds(config.questionsCount);
    const totalRounds = rounds.length;
    const order = students.map(s => s.id).sort(() => Math.random() - 0.5);

    const state: VaultCrackerState = {
      rounds,
      currentRound: 0,
      totalRounds,
      solved: new Array(totalRounds).fill(false),
      studentOrder: order,
      currentStudentId: order.length ? order[0] : null,
      lastTrapHit: null,
      usedContentHint: false,
      solveType: null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.VAULT_CRACKER,
      config,
      students,
      subject: QuizSubject.MATH,
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.VAULT_CRACKER);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    if (state.currentStudentId != null) this.setCurrentStudent(state.currentStudentId);
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  /** Record the current round's outcome. `meta` carries the shell base's
   *  timing/tries; the runtime `lastTrapHit` / `usedContentHint` / `solveType`
   *  scratch (written by the shell during play) rides along as per-game extras. */
  scoreVaultCrackerRound(
    solved: boolean,
    meta: RoundOutcomeMeta = { failedTries: 0, reactionTimeMs: null, timeToCorrectMs: null }
  ): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.VAULT_CRACKER) return;
    const state = session.gameState as VaultCrackerState;
    const round = state.rounds[state.currentRound];
    if (!round) return;

    const classId = this.gameModeService.getCurrentClassId();
    const creature = classId ? this.persistenceService.updateStreak(classId, solved) : null;
    const streak = creature?.currentStreak || 0;

    const score = this.scoringService.calculateXP({
      difficulty: round.difficulty as DifficultyLevel,
      streak,
      questionSubject: QuizSubject.MATH,
      timeRemaining: 0,
      timeLimit: 0,
      isCorrect: solved
    });
    const xpEarned = score.totalXP;

    if (classId && xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) this.gameModeService['_creature'].next(updated);
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const studentId = state.currentStudentId ?? undefined;
    session.answers.push({
      questionId: round.id,
      selectedOptionId: null,
      isCorrect: solved,
      xpEarned,
      studentId
    });
    session.totalXP += xpEarned;
    state.solved[state.currentRound] = solved;

    if (studentId != null) this.updateStudentScore(studentId, xpEarned, solved);

    this.trackRoundOutcome(MiniGameType.VAULT_CRACKER, state, solved, xpEarned, meta, {
      subSkill: round.subSkill,
      percent: round.percent,
      whole: round.whole,
      trapHit: state.lastTrapHit,
      usedContentHint: state.usedContentHint,
      solveType: solved ? state.solveType : null
    });

    this._session.next(session);
    this._lastResult.next({ isCorrect: solved, xpEarned });
  }

  /** Advance to the next round + student, or finish the game. */
  advanceVaultCrackerRound(): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.VAULT_CRACKER) return;
    const state = session.gameState as VaultCrackerState;

    state.currentRound++;
    if (state.currentRound >= state.totalRounds) {
      this.amplitudeService.trackEvent('gameWorld-vaultCracker-finished', {
        ...this.gameContext(),
        solved: state.solved.filter(Boolean).length,
        total: state.totalRounds
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    // Reset the per-round analytics scratch for the next round.
    state.lastTrapHit = null;
    state.usedContentHint = false;
    state.solveType = null;

    if (state.studentOrder.length) {
      state.currentStudentId = state.studentOrder[state.currentRound % state.studentOrder.length];
      this.setCurrentStudent(state.currentStudentId);
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  // --- Zoom Quest (find a number by zooming a place-value number line) ---

  /** Launch Zoom Quest straight into play (no setup screen). */
  startZoomQuestGame(): void {
    this.amplitudeService.trackEvent('gameWorld-zoomQuest-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.ZOOM_QUEST);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();

    const rounds = buildZoomQuestRounds(config.questionsCount);
    const totalRounds = rounds.length;
    const order = students.map(s => s.id).sort(() => Math.random() - 0.5);

    const state: ZoomQuestState = {
      rounds,
      currentRound: 0,
      totalRounds,
      solved: new Array(totalRounds).fill(false),
      studentOrder: order,
      currentStudentId: order.length ? order[0] : null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.ZOOM_QUEST,
      config,
      students,
      subject: QuizSubject.MATH,
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.ZOOM_QUEST);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    if (state.currentStudentId != null) this.setCurrentStudent(state.currentStudentId);
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  /** Record the current round's outcome for the acting student. */
  scoreZoomQuestRound(solved: boolean): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.ZOOM_QUEST) return;
    const state = session.gameState as ZoomQuestState;
    const round = state.rounds[state.currentRound];
    if (!round) return;

    const classId = this.gameModeService.getCurrentClassId();
    const creature = classId ? this.persistenceService.updateStreak(classId, solved) : null;
    const streak = creature?.currentStreak || 0;

    const score = this.scoringService.calculateXP({
      difficulty: round.difficulty as DifficultyLevel,
      streak,
      questionSubject: QuizSubject.MATH,
      timeRemaining: 0,
      timeLimit: 0,
      isCorrect: solved
    });
    const xpEarned = score.totalXP;

    if (classId && xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) this.gameModeService['_creature'].next(updated);
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const studentId = state.currentStudentId ?? undefined;
    session.answers.push({
      questionId: round.id,
      selectedOptionId: null,
      isCorrect: solved,
      xpEarned,
      studentId
    });
    session.totalXP += xpEarned;
    state.solved[state.currentRound] = solved;

    if (studentId != null) this.updateStudentScore(studentId, xpEarned, solved);

    this._session.next(session);
    this._lastResult.next({ isCorrect: solved, xpEarned });
  }

  /** Advance to the next round + student, or finish the game. */
  advanceZoomQuestRound(): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.ZOOM_QUEST) return;
    const state = session.gameState as ZoomQuestState;

    state.currentRound++;
    if (state.currentRound >= state.totalRounds) {
      this.amplitudeService.trackEvent('gameWorld-zoomQuest-finished', {
        ...this.gameContext(),
        solved: state.solved.filter(Boolean).length,
        total: state.totalRounds
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    if (state.studentOrder.length) {
      state.currentStudentId = state.studentOrder[state.currentRound % state.studentOrder.length];
      this.setCurrentStudent(state.currentStudentId);
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  // --- Treasure Grid (plot ordered pairs on a coordinate grid) ---

  /** Launch Treasure Grid straight into play (no setup screen). */
  startTreasureGridGame(): void {
    this.amplitudeService.trackEvent('gameWorld-treasureGrid-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.TREASURE_GRID);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();

    const rounds = buildTreasureGridRounds(config.questionsCount);
    const totalRounds = rounds.length;
    const order = students.map(s => s.id).sort(() => Math.random() - 0.5);

    const state: TreasureGridState = {
      rounds,
      currentRound: 0,
      totalRounds,
      solved: new Array(totalRounds).fill(false),
      studentOrder: order,
      currentStudentId: order.length ? order[0] : null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.TREASURE_GRID,
      config,
      students,
      subject: QuizSubject.MATH,
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.TREASURE_GRID);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    if (state.currentStudentId != null) this.setCurrentStudent(state.currentStudentId);
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  /** Record the current round's outcome for the acting student. */
  scoreTreasureGridRound(solved: boolean): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.TREASURE_GRID) return;
    const state = session.gameState as TreasureGridState;
    const round = state.rounds[state.currentRound];
    if (!round) return;

    const classId = this.gameModeService.getCurrentClassId();
    const creature = classId ? this.persistenceService.updateStreak(classId, solved) : null;
    const streak = creature?.currentStreak || 0;

    const score = this.scoringService.calculateXP({
      difficulty: round.difficulty as DifficultyLevel,
      streak,
      questionSubject: QuizSubject.MATH,
      timeRemaining: 0,
      timeLimit: 0,
      isCorrect: solved
    });
    const xpEarned = score.totalXP;

    if (classId && xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) this.gameModeService['_creature'].next(updated);
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const studentId = state.currentStudentId ?? undefined;
    session.answers.push({
      questionId: round.id,
      selectedOptionId: null,
      isCorrect: solved,
      xpEarned,
      studentId
    });
    session.totalXP += xpEarned;
    state.solved[state.currentRound] = solved;

    if (studentId != null) this.updateStudentScore(studentId, xpEarned, solved);

    this._session.next(session);
    this._lastResult.next({ isCorrect: solved, xpEarned });
  }

  /** Advance to the next round + student, or finish the game. */
  advanceTreasureGridRound(): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.TREASURE_GRID) return;
    const state = session.gameState as TreasureGridState;

    state.currentRound++;
    if (state.currentRound >= state.totalRounds) {
      this.amplitudeService.trackEvent('gameWorld-treasureGrid-finished', {
        ...this.gameContext(),
        solved: state.solved.filter(Boolean).length,
        total: state.totalRounds
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    if (state.studentOrder.length) {
      state.currentStudentId = state.studentOrder[state.currentRound % state.studentOrder.length];
      this.setCurrentStudent(state.currentStudentId);
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  // --- Cube Fill (measure the cubic volume of a prism) ---

  /** Launch Cube Fill straight into play (no setup screen). */
  startCubeFillGame(): void {
    this.amplitudeService.trackEvent('gameWorld-cubeFill-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.CUBE_FILL);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();

    const rounds = buildCubeFillRounds(config.questionsCount);
    const totalRounds = rounds.length;
    const order = students.map(s => s.id).sort(() => Math.random() - 0.5);

    const state: CubeFillState = {
      rounds,
      currentRound: 0,
      totalRounds,
      solved: new Array(totalRounds).fill(false),
      studentOrder: order,
      currentStudentId: order.length ? order[0] : null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.CUBE_FILL,
      config,
      students,
      subject: QuizSubject.MATH,
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.CUBE_FILL);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    if (state.currentStudentId != null) this.setCurrentStudent(state.currentStudentId);
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  /** Record the current round's outcome for the acting student. */
  scoreCubeFillRound(solved: boolean): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.CUBE_FILL) return;
    const state = session.gameState as CubeFillState;
    const round = state.rounds[state.currentRound];
    if (!round) return;

    const classId = this.gameModeService.getCurrentClassId();
    const creature = classId ? this.persistenceService.updateStreak(classId, solved) : null;
    const streak = creature?.currentStreak || 0;

    const score = this.scoringService.calculateXP({
      difficulty: round.difficulty as DifficultyLevel,
      streak,
      questionSubject: QuizSubject.MATH,
      timeRemaining: 0,
      timeLimit: 0,
      isCorrect: solved
    });
    const xpEarned = score.totalXP;

    if (classId && xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) this.gameModeService['_creature'].next(updated);
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const studentId = state.currentStudentId ?? undefined;
    session.answers.push({
      questionId: round.id,
      selectedOptionId: null,
      isCorrect: solved,
      xpEarned,
      studentId
    });
    session.totalXP += xpEarned;
    state.solved[state.currentRound] = solved;

    if (studentId != null) this.updateStudentScore(studentId, xpEarned, solved);

    this._session.next(session);
    this._lastResult.next({ isCorrect: solved, xpEarned });
  }

  /** Advance to the next round + student, or finish the game. */
  advanceCubeFillRound(): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.CUBE_FILL) return;
    const state = session.gameState as CubeFillState;

    state.currentRound++;
    if (state.currentRound >= state.totalRounds) {
      this.amplitudeService.trackEvent('gameWorld-cubeFill-finished', {
        ...this.gameContext(),
        solved: state.solved.filter(Boolean).length,
        total: state.totalRounds
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    if (state.studentOrder.length) {
      state.currentStudentId = state.studentOrder[state.currentRound % state.studentOrder.length];
      this.setCurrentStudent(state.currentStudentId);
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  // --- Balance It (metric measurement conversion on a balance scale) ---

  /** Launch Balance It straight into play (no setup screen). */
  startBalanceItGame(): void {
    this.amplitudeService.trackEvent('gameWorld-balanceIt-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.BALANCE_IT);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();

    const rounds = buildBalanceItRounds(config.questionsCount);
    const totalRounds = rounds.length;
    const order = students.map(s => s.id).sort(() => Math.random() - 0.5);

    const state: BalanceItState = {
      rounds,
      currentRound: 0,
      totalRounds,
      solved: new Array(totalRounds).fill(false),
      studentOrder: order,
      currentStudentId: order.length ? order[0] : null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.BALANCE_IT,
      config,
      students,
      subject: QuizSubject.MATH,
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.BALANCE_IT);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    if (state.currentStudentId != null) this.setCurrentStudent(state.currentStudentId);
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  /** Record the current round's outcome for the acting student. */
  scoreBalanceItRound(solved: boolean): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.BALANCE_IT) return;
    const state = session.gameState as BalanceItState;
    const round = state.rounds[state.currentRound];
    if (!round) return;

    const classId = this.gameModeService.getCurrentClassId();
    const creature = classId ? this.persistenceService.updateStreak(classId, solved) : null;
    const streak = creature?.currentStreak || 0;

    const score = this.scoringService.calculateXP({
      difficulty: round.difficulty as DifficultyLevel,
      streak,
      questionSubject: QuizSubject.MATH,
      timeRemaining: 0,
      timeLimit: 0,
      isCorrect: solved
    });
    const xpEarned = score.totalXP;

    if (classId && xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) this.gameModeService['_creature'].next(updated);
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const studentId = state.currentStudentId ?? undefined;
    session.answers.push({
      questionId: round.id,
      selectedOptionId: null,
      isCorrect: solved,
      xpEarned,
      studentId
    });
    session.totalXP += xpEarned;
    state.solved[state.currentRound] = solved;

    if (studentId != null) this.updateStudentScore(studentId, xpEarned, solved);

    this._session.next(session);
    this._lastResult.next({ isCorrect: solved, xpEarned });
  }

  /** Advance to the next round + student, or finish the game. */
  advanceBalanceItRound(): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.BALANCE_IT) return;
    const state = session.gameState as BalanceItState;

    state.currentRound++;
    if (state.currentRound >= state.totalRounds) {
      this.amplitudeService.trackEvent('gameWorld-balanceIt-finished', {
        ...this.gameContext(),
        solved: state.solved.filter(Boolean).length,
        total: state.totalRounds
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    if (state.studentOrder.length) {
      state.currentStudentId = state.studentOrder[state.currentRound % state.studentOrder.length];
      this.setCurrentStudent(state.currentStudentId);
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  // --- Power Burst (evaluate exponents) ---

  /** Launch Power Burst straight into play (no setup screen). */
  startPowerBurstGame(): void {
    this.amplitudeService.trackEvent('gameWorld-powerBurst-launch', { ...this.gameContext() });
    const config = getMiniGameConfig(MiniGameType.POWER_BURST);
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();

    const rounds = buildPowerBurstRounds(config.questionsCount);
    const totalRounds = rounds.length;
    const order = students.map(s => s.id).sort(() => Math.random() - 0.5);

    const state: PowerBurstState = {
      rounds,
      currentRound: 0,
      totalRounds,
      solved: new Array(totalRounds).fill(false),
      studentOrder: order,
      currentStudentId: order.length ? order[0] : null
    };

    const session: MiniGameSession = {
      gameType: MiniGameType.POWER_BURST,
      config,
      students,
      subject: QuizSubject.MATH,
      questions: [],
      currentQuestionIndex: 0,
      answers: [],
      totalXP: 0,
      startedAt: new Date(),
      gameState: state
    };

    this._currentGameType.next(MiniGameType.POWER_BURST);
    this._session.next(session);
    this._lastResult.next(null);
    this.initStudentScores(students);
    this.studentRotationIndex = 0;
    if (state.currentStudentId != null) this.setCurrentStudent(state.currentStudentId);
    this.gameModeService.setPhase(GamePhase.MINI_GAME_ACTIVE);
  }

  /** Record the current round's outcome for the acting student. */
  scorePowerBurstRound(solved: boolean): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.POWER_BURST) return;
    const state = session.gameState as PowerBurstState;
    const round = state.rounds[state.currentRound];
    if (!round) return;

    const classId = this.gameModeService.getCurrentClassId();
    const creature = classId ? this.persistenceService.updateStreak(classId, solved) : null;
    const streak = creature?.currentStreak || 0;

    const score = this.scoringService.calculateXP({
      difficulty: round.difficulty as DifficultyLevel,
      streak,
      questionSubject: QuizSubject.MATH,
      timeRemaining: 0,
      timeLimit: 0,
      isCorrect: solved
    });
    const xpEarned = score.totalXP;

    if (classId && xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) this.gameModeService['_creature'].next(updated);
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const studentId = state.currentStudentId ?? undefined;
    session.answers.push({
      questionId: round.id,
      selectedOptionId: null,
      isCorrect: solved,
      xpEarned,
      studentId
    });
    session.totalXP += xpEarned;
    state.solved[state.currentRound] = solved;

    if (studentId != null) this.updateStudentScore(studentId, xpEarned, solved);

    this._session.next(session);
    this._lastResult.next({ isCorrect: solved, xpEarned });
  }

  /** Advance to the next round + student, or finish the game. */
  advancePowerBurstRound(): void {
    const session = this._session.getValue();
    if (!session || session.gameType !== MiniGameType.POWER_BURST) return;
    const state = session.gameState as PowerBurstState;

    state.currentRound++;
    if (state.currentRound >= state.totalRounds) {
      this.amplitudeService.trackEvent('gameWorld-powerBurst-finished', {
        ...this.gameContext(),
        solved: state.solved.filter(Boolean).length,
        total: state.totalRounds
      });
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    if (state.studentOrder.length) {
      state.currentStudentId = state.studentOrder[state.currentRound % state.studentOrder.length];
      this.setCurrentStudent(state.currentStudentId);
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  getSession(): MiniGameSession | null {
    return this._session.getValue();
  }

  getCurrentQuestion(): GameQuestion | null {
    const session = this._session.getValue();
    if (!session) return null;
    return session.questions[session.currentQuestionIndex] || null;
  }

  submitAnswer(optionId: 'A' | 'B' | 'C' | 'D' | null, extraData?: { studentId?: number; teamId?: 'A' | 'B' }): void {
    const session = this._session.getValue();
    if (!session) return;

    this.stopTimer();
    const question = session.questions[session.currentQuestionIndex];
    if (!question) return;

    const isCorrect = optionId === question.correctOptionId;
    const classId = this.gameModeService.getCurrentClassId();
    if (!classId) return;

    // Update streak
    const creature = this.persistenceService.updateStreak(classId, isCorrect);
    const streak = creature?.currentStreak || 0;

    // Calculate base XP
    const timeRemaining = this._timerValue.getValue();
    const timeLimit = session.gameState?.fuseTime || session.config.timePerQuestion || 30;
    let score = this.scoringService.calculateXP({
      difficulty: question.difficulty,
      streak,
      questionSubject: question.subject,
      timeRemaining,
      timeLimit,
      isCorrect
    });

    // Apply game-specific XP modifiers
    let xpEarned = score.totalXP;
    xpEarned = this.applyGameModifiers(session, xpEarned, isCorrect);

    // Update creature XP
    if (xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) {
        this.gameModeService['_creature'].next(updated);
      }
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    // Record answer
    const answer: MiniGameAnswer = {
      questionId: question.id,
      selectedOptionId: optionId,
      isCorrect,
      xpEarned,
      studentId: extraData?.studentId,
      teamId: extraData?.teamId
    };
    session.answers.push(answer);
    session.totalXP += xpEarned;

    // Update game-specific state
    this.updateGameState(session, isCorrect, extraData);

    // Update leaderboard scores
    const scoredStudentId = extraData?.studentId || this.getRotationStudentId(session);
    if (scoredStudentId) {
      this.updateStudentScore(scoredStudentId, xpEarned, isCorrect);
    }

    this._session.next(session);
    this._lastResult.next({ isCorrect, xpEarned });
  }

  nextQuestion(): void {
    const session = this._session.getValue();
    if (!session) return;

    session.currentQuestionIndex++;
    if (session.currentQuestionIndex >= session.questions.length) {
      const finishedKey = this.gameEventName[session.gameType];
      if (finishedKey) {
        this.amplitudeService.trackEvent(`gameWorld-${finishedKey}-finished`, { ...this.gameContext() });
      }
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }

    this._lastResult.next(null);
    this._session.next(session);
  }

  /** Submit answer for one team in tug of war (simultaneous mode) */
  submitTugOfWarTeamAnswer(
    teamId: 'A' | 'B',
    optionId: 'A' | 'B' | 'C' | 'D' | null,
    questionIndex: number
  ): { isCorrect: boolean; xpEarned: number } {
    const session = this._session.getValue();
    if (!session) return { isCorrect: false, xpEarned: 0 };

    const question = session.questions[questionIndex];
    if (!question) return { isCorrect: false, xpEarned: 0 };

    const isCorrect = optionId === question.correctOptionId;
    const classId = this.gameModeService.getCurrentClassId();
    if (!classId) return { isCorrect: false, xpEarned: 0 };

    const creature = this.persistenceService.updateStreak(classId, isCorrect);
    const streak = creature?.currentStreak || 0;

    const timeRemaining = this._timerValue.getValue();
    const timeLimit = session.config.timePerQuestion || 25;
    let score = this.scoringService.calculateXP({
      difficulty: question.difficulty,
      streak,
      questionSubject: question.subject,
      timeRemaining,
      timeLimit,
      isCorrect
    });

    let xpEarned = score.totalXP;

    if (xpEarned > 0) {
      const updated = this.persistenceService.updateXP(classId, xpEarned);
      if (updated) {
        this.gameModeService['_creature'].next(updated);
      }
    } else if (creature) {
      this.gameModeService['_creature'].next(creature);
    }

    const answer: MiniGameAnswer = {
      questionId: question.id,
      selectedOptionId: optionId,
      isCorrect,
      xpEarned,
      teamId
    };
    session.answers.push(answer);
    session.totalXP += xpEarned;

    // Update rope position and team score
    this.updateTugOfWarState(session, isCorrect, { teamId });

    this._session.next(session);
    return { isCorrect, xpEarned };
  }

  finishMiniGame(): void {
    this._session.next(null);
    this._currentGameType.next(null);
    this._lastResult.next(null);
    this._studentScores.next([]);
    this._currentStudentId.next(null);
    this.studentRotationIndex = 0;
    this.stopTimer();

    if (this._autoCycleEnabled.getValue()) {
      // Auto-cycle: start next game immediately
      this.startNextAutoCycleGame();
    } else {
      // Manual: go back to the top-level category picker (Trivia /
      // Fun Games). User picks again from there each session.
      this.gameModeService.setPhase(GamePhase.GAME_CATEGORY);
    }
  }

  // --- Timer ---

  startTimer(seconds: number): void {
    this.stopTimer();
    this._timerValue.next(seconds);
    this.timerInterval = setInterval(() => {
      const current = this._timerValue.getValue();
      if (current <= 0) {
        this.stopTimer();
        this._timerExpired.next();
      } else {
        this._timerValue.next(current - 1);
      }
    }, 1000);
  }

  stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  getTimerValue(): number {
    return this._timerValue.getValue();
  }

  // --- Game State Initialization ---

  private initGameState(gameType: MiniGameType, students: MiniGameStudent[], config: any): any {
    switch (gameType) {
      case MiniGameType.SPIN_WHEEL:
        return {
          currentAngle: 0,
          targetAngle: 0,
          isSpinning: false,
          selectedStudentId: null,
          studentResults: students.map(s => ({ studentId: s.id, correct: 0, total: 0 }))
        } as SpinWheelState;

      case MiniGameType.TUG_OF_WAR:
        return {
          ropePosition: 0,
          currentRound: 0,
          totalRounds: Math.floor(config.questionsCount / 2),
          teamASubmitted: false,
          teamBSubmitted: false,
          teamACorrect: null,
          teamBCorrect: null,
          pullHistory: []
        } as TugOfWarState;

      case MiniGameType.LIGHTNING_ROUND:
        return {
          comboCount: 0,
          maxCombo: 0,
          comboMultiplier: 1,
          flashResult: null,
          intensity: 0
        } as LightningRoundState;

      case MiniGameType.BOMB_PASS:
        const order = students.map(s => s.id).sort(() => Math.random() - 0.5);
        return {
          currentHolderId: order[0],
          fuseTime: this.getRandomFuseTime(),
          explosions: [],
          passOrder: order,
          currentPassIndex: 0
        } as BombPassState;

      case MiniGameType.MYSTERY_BOX:
        return {
          boxes: this.generateMysteryBoxes(12),
          currentModifier: null,
          modifierRevealed: false
        } as MysteryBoxState;

      default:
        return {};
    }
  }

  private generateMysteryBoxes(count: number): { id: number; modifier: BoxModifier; isRevealed: boolean; isUsed: boolean }[] {
    const bonusPool = [
      BoxModifier.DOUBLE_XP,
      BoxModifier.TRIPLE_XP,
      BoxModifier.FREE_PASS,
      BoxModifier.EXTRA_TIME,
      BoxModifier.SHIELD,
      BoxModifier.DOUBLE_XP
    ];
    const bamboozlePool = [
      BoxModifier.HALF_TIME,
      BoxModifier.FREEZE,
      BoxModifier.STEAL,
      BoxModifier.MINUS_SCORE,
      BoxModifier.REVERSE,
      BoxModifier.HALF_TIME
    ];
    const modifiers = [...bonusPool, ...bamboozlePool];
    // Shuffle modifiers
    for (let i = modifiers.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [modifiers[i], modifiers[j]] = [modifiers[j], modifiers[i]];
    }
    return modifiers.slice(0, count).map((modifier, i) => ({
      id: i,
      modifier,
      isRevealed: false,
      isUsed: false
    }));
  }

  getRandomFuseTime(): number {
    return Math.floor(Math.random() * 8) + 8; // 8-15 seconds
  }

  // --- Game-Specific Modifiers ---

  private applyGameModifiers(session: MiniGameSession, baseXP: number, isCorrect: boolean): number {
    let xp = baseXP;

    switch (session.gameType) {
      case MiniGameType.SPIN_WHEEL:
        if (isCorrect) xp = Math.round(xp * session.config.xpMultiplier);
        break;

      case MiniGameType.LIGHTNING_ROUND:
        const lState = session.gameState as LightningRoundState;
        if (isCorrect) xp = Math.round(xp * lState.comboMultiplier);
        break;

      case MiniGameType.BOMB_PASS:
        const bState = session.gameState as BombPassState;
        if (isCorrect && this._timerValue.getValue() <= 3) {
          xp += 5; // hot potato bonus
        }
        break;

      case MiniGameType.MYSTERY_BOX:
        const mState = session.gameState as MysteryBoxState;
        if (mState.currentModifier) {
          switch (mState.currentModifier) {
            // Bonus cards
            case BoxModifier.DOUBLE_XP:
              xp = xp * 2;
              break;
            case BoxModifier.TRIPLE_XP:
              xp = xp * 3;
              break;
            case BoxModifier.FREE_PASS:
              if (!isCorrect) xp = 5;
              break;
            case BoxModifier.EXTRA_TIME:
              // Time handled in component (45s), XP normal
              break;
            case BoxModifier.SHIELD:
              if (!isCorrect) xp = 0;
              break;
            // Bamboozle cards
            case BoxModifier.HALF_TIME:
              // Time handled in component (15s), XP normal
              break;
            case BoxModifier.FREEZE:
              // Time handled in component (10s), XP normal
              break;
            case BoxModifier.STEAL:
              if (isCorrect) xp = xp * 2;
              else xp = -10;
              break;
            case BoxModifier.MINUS_SCORE:
              xp = xp - 15;
              break;
            case BoxModifier.REVERSE:
              if (isCorrect) xp = -5;
              else xp = 5;
              break;
          }
        }
        break;
    }

    return xp;
  }

  // --- Game-Specific State Updates ---

  private updateGameState(session: MiniGameSession, isCorrect: boolean, extraData?: any): void {
    switch (session.gameType) {
      case MiniGameType.SPIN_WHEEL:
        this.updateSpinWheelState(session, isCorrect, extraData?.studentId);
        break;
      case MiniGameType.TUG_OF_WAR:
        this.updateTugOfWarState(session, isCorrect, extraData);
        break;
      case MiniGameType.LIGHTNING_ROUND:
        this.updateLightningState(session, isCorrect);
        break;
      case MiniGameType.BOMB_PASS:
        this.updateBombPassState(session, isCorrect);
        break;
    }
  }

  private updateSpinWheelState(session: MiniGameSession, isCorrect: boolean, studentId?: number): void {
    const state = session.gameState as SpinWheelState;
    if (studentId) {
      const result = state.studentResults.find(r => r.studentId === studentId);
      if (result) {
        result.total++;
        if (isCorrect) result.correct++;
      }
    }
    state.isSpinning = false;
    state.selectedStudentId = null;
  }

  private updateTugOfWarState(session: MiniGameSession, isCorrect: boolean, extraData?: any): void {
    const state = session.gameState as TugOfWarState;
    const teamId: 'A' | 'B' = extraData?.teamId || 'A';
    const pull = isCorrect ? 10 : -3;

    if (teamId === 'A') {
      state.ropePosition -= pull;
      if (session.teams && isCorrect) session.teams.teamA.score++;
      state.teamASubmitted = true;
      state.teamACorrect = isCorrect;
    } else {
      state.ropePosition += pull;
      if (session.teams && isCorrect) session.teams.teamB.score++;
      state.teamBSubmitted = true;
      state.teamBCorrect = isCorrect;
    }
    state.pullHistory.push({ team: teamId, correct: isCorrect });
  }

  /** Advance tug of war to next round, resetting per-round state */
  advanceTugOfWarRound(): void {
    const session = this._session.getValue();
    if (!session) return;
    const state = session.gameState as TugOfWarState;
    state.currentRound++;
    state.teamASubmitted = false;
    state.teamBSubmitted = false;
    state.teamACorrect = null;
    state.teamBCorrect = null;

    if (state.currentRound >= state.totalRounds) {
      const finishedKey = this.gameEventName[session.gameType];
      if (finishedKey) {
        this.amplitudeService.trackEvent(`gameWorld-${finishedKey}-finished`, { ...this.gameContext() });
      }
      this.gameModeService.setPhase(GamePhase.MINI_GAME_SUMMARY);
      return;
    }
    this._lastResult.next(null);
    this._session.next(session);
  }

  private updateLightningState(session: MiniGameSession, isCorrect: boolean): void {
    const state = session.gameState as LightningRoundState;
    if (isCorrect) {
      state.comboCount++;
      if (state.comboCount > state.maxCombo) state.maxCombo = state.comboCount;
      state.comboMultiplier = state.comboCount >= 6 ? 3 : state.comboCount >= 4 ? 2 : state.comboCount >= 2 ? 1.5 : 1;
      state.intensity = Math.min(3, Math.floor(state.comboCount / 2));
      state.flashResult = 'correct';
    } else {
      state.comboCount = 0;
      state.comboMultiplier = 1;
      state.intensity = 0;
      state.flashResult = 'incorrect';
    }
  }

  private updateBombPassState(session: MiniGameSession, isCorrect: boolean): void {
    const state = session.gameState as BombPassState;
    if (!isCorrect) {
      state.explosions.push({
        studentId: state.currentHolderId,
        questionIndex: session.currentQuestionIndex
      });
    }
    // Pass to next student
    state.currentPassIndex = (state.currentPassIndex + 1) % state.passOrder.length;
    state.currentHolderId = state.passOrder[state.currentPassIndex];
    state.fuseTime = this.getRandomFuseTime();
  }

  // --- Leaderboard Tracking ---

  private initStudentScores(students: MiniGameStudent[]): void {
    const scores: StudentScoreEntry[] = students.map((student, i) => ({
      student,
      xp: 0,
      correctCount: 0,
      totalAnswered: 0,
      rank: i + 1,
      previousRank: i + 1,
      rankChange: 'new' as const,
      lastXpGain: 0,
      streak: 0
    }));
    this._studentScores.next(scores);
  }

  private updateStudentScore(studentId: number, xpEarned: number, isCorrect: boolean): void {
    const scores = this._studentScores.getValue().map(s => ({ ...s }));
    const entry = scores.find(s => s.student.id === studentId);
    if (!entry) return;

    entry.previousRank = entry.rank;
    entry.xp += Math.max(0, xpEarned);
    entry.totalAnswered++;
    entry.lastXpGain = xpEarned;
    if (isCorrect) {
      entry.correctCount++;
      entry.streak++;
    } else {
      entry.streak = 0;
    }

    // Re-sort by XP descending, then by correctCount, then by original order
    scores.sort((a, b) => {
      if (b.xp !== a.xp) return b.xp - a.xp;
      if (b.correctCount !== a.correctCount) return b.correctCount - a.correctCount;
      return a.rank - b.rank;
    });

    // Update ranks and detect changes
    scores.forEach((s, i) => {
      s.rank = i + 1;
      if (s.previousRank < s.rank) {
        s.rankChange = 'down';
      } else if (s.previousRank > s.rank) {
        s.rankChange = 'up';
      } else {
        s.rankChange = s.student.id === studentId && xpEarned > 0 ? 'same' : 'same';
      }
    });

    this._currentStudentId.next(studentId);
    this._studentScores.next(scores);
  }

  private getRotationStudentId(session: MiniGameSession): number | null {
    const students = session.students;
    if (!students.length) return null;
    const student = students[this.studentRotationIndex % students.length];
    this.studentRotationIndex++;
    return student.id;
  }

  setCurrentStudent(studentId: number): void {
    this._currentStudentId.next(studentId);
  }
}
